import BaseController from "./base_controller"

export default class extends BaseController {
  static targets = ["dateSection", "dosesSection", "datePreview", "dosesPreview", "calculatedDoses", "calculatedDate"]
  static values = { protocolId: String, startDate: String, locale: String }
  
  declare readonly dateSectionTargets: HTMLElement[]
  declare readonly dosesSectionTargets: HTMLElement[]
  declare readonly datePreviewTarget: HTMLElement
  declare readonly dosesPreviewTarget: HTMLElement
  declare readonly calculatedDosesTargets: HTMLElement[]
  declare readonly calculatedDateTargets: HTMLElement[]
  declare protocolIdValue: string
  declare startDateValue: string
  declare localeValue: string
  
  connect(): void {
    this.updateVisibility()
    this.performCalculations()
  }
  
  updateEndType(): void {
    this.updateVisibility()
    this.performCalculations()
  }
  
  updateVisibility(): void {
    const endType = this.getSelectedEndType()
    
    if (endType === 'date') {
      this.showDateSection()
      this.hideDosesSection()
    } else {
      this.hideDateSection()
      this.showDosesSection()
    }
  }
  
  showDateSection(): void {
    this.dateSectionTargets.forEach((section: HTMLElement) => {
      section.style.display = 'block'
    })
  }
  
  hideDateSection(): void {
    this.dateSectionTargets.forEach((section: HTMLElement) => {
      section.style.display = 'none'
    })
  }
  
  showDosesSection(): void {
    this.dosesSectionTargets.forEach((section: HTMLElement) => {
      section.style.display = 'block'
    })
  }
  
  hideDosesSection(): void {
    this.dosesSectionTargets.forEach((section: HTMLElement) => {
      section.style.display = 'none'
    })
  }
  
  calculateFromDate(): void {
    // Real-time calculation when user changes end date
    this.performCalculations()
  }
  
  calculateFromDoses(): void {
    // Real-time calculation when user changes target doses
    this.performCalculations()
  }

  
  async performCalculations(): Promise<void> {
    const endType = this.getSelectedEndType()
    
    if (endType === 'date') {
      await this.calculateDosesFromDate()
    } else {
      await this.calculateDateFromDoses()
    }
  }
  
  async calculateDosesFromDate(): Promise<void> {
    const startDate = this.getStartDate()
    const endDate = this.getEndDate()
    const protocolId = this.getCurrentProtocolId()
    if (!startDate || !endDate || !protocolId) {
      this.updateCalculatedDoses('--')
      return
    }
    
    try {
      const csrfToken = this.getCSRFToken()
      const headers: HeadersInit = { 
        'Content-Type': 'application/json'
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken
      }
      
      const locale = this.localeValue || 'en'
      const response = await fetch(`/${locale}/api/calculate-doses`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          protocol_id: protocolId
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        this.updateCalculatedDoses(data.target_doses || '--')
      } else {
        this.updateCalculatedDoses('--')
      }
    } catch (error) {
      this.handleError(error, 'calculateDoses')
      this.updateCalculatedDoses('--')
    }
  }
  
  async calculateDateFromDoses(): Promise<void> {
    const startDate = this.getStartDate()
    const targetDoses = this.getTargetDoses()
    const protocolId = this.getCurrentProtocolId()
    if (!startDate || !targetDoses || !protocolId) {
      this.updateCalculatedDate('--')
      return
    }
    
    try {
      const csrfToken = this.getCSRFToken()
      const headers: HeadersInit = { 
        'Content-Type': 'application/json'
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken
      }
      
      const locale = this.localeValue || 'en'
      const response = await fetch(`/${locale}/api/calculate-end-date`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          start_date: startDate,
          target_doses: targetDoses,
          protocol_id: protocolId
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        this.updateCalculatedDate(data.end_date || '--')
      } else {
        this.updateCalculatedDate('--')
      }
    } catch (error) {
      this.handleError(error, 'calculateEndDate')
      this.updateCalculatedDate('--')
    }
  }
  
  getSelectedEndType(): string {
    const checked = this.element.querySelector<HTMLInputElement>('input[name*="microdosing_end_type"]:checked')
    return checked ? checked.value : 'doses'
  }
  
  getStartDate(): string | null {
    const input = this.element.querySelector<HTMLInputElement>('input[name*="microdosing_start_date"]')
    return input ? input.value : null
  }
  
  getEndDate(): string | null {
    const input = this.element.querySelector<HTMLInputElement>('input[name*="microdosing_end_date"]')
    return input ? input.value : null
  }
  
  getTargetDoses(): number | null {
    const input = this.element.querySelector<HTMLInputElement>('input[name*="microdosing_target_doses"]')
    return input && input.value ? parseInt(input.value, 10) : null
  }
  
  getCurrentProtocolId(): string | null {
    // Try to get current value from select element first (for real-time updates)
    const select = this.element.querySelector<HTMLSelectElement>('select[name*="current_protocol_id"]')
    if (select && select.value) {
      return select.value
    }
    // Fallback to data value if select not found or empty
    return this.protocolIdValue || null
  }
  
  updateCalculatedDoses(value: string | number): void {
    this.calculatedDosesTargets.forEach((target: HTMLElement) => {
      target.textContent = String(value)
    })
  }
  
  updateCalculatedDate(value: string): void {
    this.calculatedDateTargets.forEach((target: HTMLElement) => {
      target.textContent = value
    })
  }
  
  getCSRFToken(): string | null {
    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : null
  }
  
  protected handleError(error: unknown, context: string): void {
    super.handleError(error as Error, context)
  }
}
